<script setup lang="ts">
import { CtButton, CtIcon } from '@camelot/components'
import { storeToRefs } from 'pinia'
import { onBeforeMount, ref, watch } from 'vue'
import FAQ from '~/components/banner/FAQ.vue'
import DateAndTimeSelection from '~/components/input/date-time/DateAndTimeSelection.vue'
import LocationSelection from '~/components/input/location/LocationSelection.vue'
import VehicleCardList from '~/components/vehicle/VehicleCardList.vue'
import { useDateTimeStore } from '~/stores/datetime'
import { useLocationStore } from '~/stores/location'
import { useOfferStore } from '~/stores/offer'
import { useReferralStore } from '~/stores/referral'
import { useStepStore } from '~/stores/step'
import { translate } from '~/composables/translate/translations'
import SellingArguments from '~/components/banner/info/SellingArguments.vue'
import { CITIES, CITIES_RECORD, DEFAULT_CITY } from '~/composables/city'
import useReferral from '~/composables/use-referral'

const { setInitialStepFrom } = useStepStore()
const locationStore = useLocationStore()
const dateTimeStore = useDateTimeStore()
const offerStore = useOfferStore()
const referralStore = useReferralStore()

const { startLoadingOffers, loadingOffers } = storeToRefs(offerStore)

useReferral()

onBeforeMount(() => {
  const { params, query } = useRoute()
  const CITIES_ALLOWED: Set<string> = new Set(CITIES.map(c => c.id))
  let city: string = params._city?.toString()?.toLowerCase() ?? DEFAULT_CITY
  city = CITIES_ALLOWED.has(city) ? city : DEFAULT_CITY
  locationStore.city = CITIES_RECORD[city]

  referralStore.$patch({
    leadGenerator: query?.utm_source as string,
    leadPlatform: query?.utm_medium as string,
  })

  setInitialStepFrom('category')
})

const offersContainer = ref<HTMLElement>()

const isLoading = computed<boolean>(() => startLoadingOffers.value || loadingOffers.value)

function fetchOffers() { startLoadingOffers.value = true }

function scrollToOffersContainer() { offersContainer.value?.scrollIntoView({ behavior: 'smooth' }) }

if (screen.width <= 640) {
  watch(loadingOffers, (isLoading) => {
    if (!isLoading)
      scrollToOffersContainer()
  })
}
</script>

<template>
  <section class="flex flex-col">
    <form class="gap-y-[32px] gap-x-[8px] ct-card flex flex-col xl:flex-row flex-wrap pb-44 pt-32 xs:px-[22px]">
      <LocationSelection class="xl:w-[40%] md:min-w-[460px]" />

      <DateAndTimeSelection class="xl:w-5/12 md:min-w-[530px]" />

      <CtButton
        v-if="!offerStore.possibleOffers.length"
        :disabled="!locationStore.isValid || !dateTimeStore.areDatesValid || isLoading"
        :loading="isLoading && dateTimeStore.areDatesValid"
        class="xl:mt-[72px] xl:mr-auto mt-16"
        @click="fetchOffers"
      >
        {{ translate('prebook_search_button') }}
      </CtButton>
    </form>

    <SellingArguments class="mt-40 lg:order-[9998]" />

    <section id="offers" ref="offersContainer">
      <CtIcon v-if="offerStore.loadingOffers" class="h-[100px] w-[100px] animate-pulse mx-auto" name="brand_free2move" />
      <VehicleCardList v-else-if="offerStore.possibleOffers.length" class="mt-20 pt-20" />
    </section>
    <FAQ class="order-last" />
  </section>
</template>

<style lang="scss" scoped>
.address-info {
  @apply text-black-40 text-xs;

  :deep(a) {
    @apply ct-link text-xs
  }
}
</style>
